import { useState } from 'react'

import { useFragment } from '@apollo/client'
import { F0AvatarFile, F0Box, F0Button, F0Text } from '@factorialco/f0-react'
import { Delete as DeleteIcon, Download as DownloadIcon } from '@factorialco/f0-react/icons/app'
import { InboxTaskListItemCompletionFragmentDoc } from 'generated/resources/graphql'

import DropZone from 'modules/core/components/DropZone'
import i18n from 'modules/core/lib/i18n'

import { StepActionContentComponent } from 'modules/inbox_tasks/tasks/employeeLifecycleStepAction/helpers'

import { useUploadDocument } from './useUploadDocument'

// The content types the `upload_document` StepAction accepts — kept in sync
// with EmployeeLifecycle::Dtos::StepAction::Upload::ACCEPTED_CONTENT_TYPES.
//
// react-dropzone v14 passes this to the browser's `showOpenFilePicker`
// (FS Access API), which rejects MIME types mapped to an empty extension
// array — so we declare extensions explicitly here rather than using the
// shared `acceptedContentTypes` helper, which would emit empty arrays and
// silently prevent the file picker from ever opening.
const ACCEPT = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
}

const UploadDocumentContent: StepActionContentComponent = ({
  taskId,
  stepActionId,
  files = [],
}) => {
  const { addFiles, removeFile } = useUploadDocument(stepActionId ?? '')

  // Read the task status from Apollo cache so the panel reflects already-done
  // (or otherwise terminal) tasks when re-opened: any non-`todo` status locks
  // the panel — dropzone and per-file delete hidden, leaving Download only.
  // `done` covers the happy path (the inbox shell drives mark-as-done; the
  // backend `complete` verb rejects an upload_document step with zero
  // documents via `StepActions::Complete`'s guard). `discarded` (and any
  // future terminal statuses the shell adds) are equally non-actionable —
  // we must not let the assignee upload or remove files after the workflow
  // step has been cancelled.
  const { data: taskInfo } = useFragment({
    fragment: InboxTaskListItemCompletionFragmentDoc,
    fragmentName: 'InboxTaskListItemCompletion',
    from: { __typename: 'TasksInboxTask', id: taskId },
  })
  const isLocked = taskInfo?.status !== undefined && taskInfo.status !== 'todo'

  const [uploading, setUploading] = useState(false)
  const [removingIds, setRemovingIds] = useState<string[]>([])

  if (!stepActionId) return null

  const handleDrop = async (accepted: File[]) => {
    if (accepted.length === 0 || isLocked) return

    setUploading(true)
    try {
      await addFiles(accepted)
    } finally {
      setUploading(false)
    }
  }

  const handleRemove = async (fileId: string) => {
    if (isLocked) return

    setRemovingIds((prev) => [...prev, fileId])
    try {
      await removeFile(fileId)
    } finally {
      setRemovingIds((prev) => prev.filter((id) => id !== fileId))
    }
  }

  const visibleFiles = files.filter((file) => !removingIds.includes(file.id))

  return (
    <F0Box display="flex" flexDirection="column" gap="md" overflowX="hidden">
      {!isLocked && (
        <DropZone
          multiple
          accept={ACCEPT}
          loading={uploading}
          subtitle={i18n.t('employee_lifecycle.workflows.upload_document.accepted_types')}
          onDrop={(accepted) => {
            void handleDrop(accepted)
          }}
        />
      )}
      {visibleFiles.length > 0 && (
        <F0Box display="flex" flexDirection="column" gap="sm">
          {visibleFiles.map((file) => (
            <F0Box
              key={file.id}
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap="sm"
              padding="sm"
              border="default"
              borderRadius="md"
            >
              <F0AvatarFile
                file={{ name: file.filename, type: file.contentType ?? '' }}
                size="md"
              />
              <F0Box grow>
                <F0Text variant="body" content={file.filename} />
              </F0Box>
              <F0Button
                icon={DownloadIcon}
                label={i18n.t('common.download')}
                variant="outline"
                hideLabel
                onClick={() => window.open(file.fileUrl, '_blank', 'noopener,noreferrer')}
              />
              {!isLocked && (
                <F0Button
                  icon={DeleteIcon}
                  label={i18n.t('common.delete')}
                  variant="outline"
                  hideLabel
                  onClick={() => {
                    void handleRemove(file.id)
                  }}
                />
              )}
            </F0Box>
          ))}
        </F0Box>
      )}
    </F0Box>
  )
}

export default UploadDocumentContent
